import { Center, CircularProgress, CircularProgressLabel } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { FC, useContext } from "react";

import { getContractApi, getUsageApi } from "../../apiConfiguration";
import { OrganisationContext } from "../../providers/OrganisationProvider";
import { humanizeSeconds } from "../../utils/functions";

interface UsageStatisticsProps {
  username?: string;
  typeIsLive?: boolean;
  hideCrashed?: boolean;
  modelNameSearch?: string;
  simulationNameSearch?: string;
  userNameSearch?: string;
}

const UsageStatistics: FC<UsageStatisticsProps> = ({
  username,
  typeIsLive,
  hideCrashed = false,
  modelNameSearch,
  simulationNameSearch,
  userNameSearch,
}) => {
  const { organisation } = useContext(OrganisationContext);
  const contractApi = getContractApi();
  const usageApi = getUsageApi();

  const contractResponse = useQuery({
    queryKey: ["contract", organisation.uniqueId],
    queryFn: () =>
      contractApi.contractsList({
        organisationUniqueId: organisation.uniqueId,
      }),
  });

  const usageResponse = useQuery({
    queryKey: ["usageStatistics", organisation.uniqueId],
    queryFn: () => usageApi.usageStatistics(),
  });

  const usageStatistics = usageResponse.isSuccess && usageResponse.data ? usageResponse.data : null;

  const contract =
    contractResponse.isSuccess && contractResponse.data && contractResponse.data.results.length
      ? contractResponse.data.results[0]
      : null;

  const isUnlimited = contract && contract.hoursBought && contract.hoursBought >= 9999;

  const hoursUsedPct =
    contract &&
    contract.hoursUsed !== undefined &&
    contract.hoursBought !== undefined &&
    contract.hoursBought > 0
      ? Math.round(Math.max(0, Math.min((100 * contract.hoursUsed) / contract.hoursBought, 100)))
      : null;

  return (
    <>
      <p>An overview of recently finished simulations.</p>
      {isUnlimited ? (
        <p>Organisation {organisation.name} can use unlimited simulation hours.</p>
      ) : hoursUsedPct !== null ? (
        <>
          <p>
            Contract hours used: {Math.round(contract!.hoursUsed!)} of{" "}
            {Math.round(contract!.hoursBought)}
          </p>
          <Center>
            <CircularProgress value={hoursUsedPct} size="10rem">
              <CircularProgressLabel>{hoursUsedPct}%</CircularProgressLabel>
            </CircularProgress>
          </Center>
        </>
      ) : null}
      {!isUnlimited && usageStatistics ? (
        <>
          <p style={{ margin: 0 }}>Statistics for these simulations (including crashed):</p>
          <p style={{ margin: 0 }}>Total number of calculations: {usageStatistics.totalSessions}</p>
          <p style={{ margin: 0 }}>
            Total duration: {humanizeSeconds(usageStatistics.totalDuration)}
          </p>
        </>
      ) : null}
    </>
  );
};

export default UsageStatistics;
